import { useQSet } from '@exam-management/common/qset-provider';
import { getDifficultyType } from '@exam-management/components';
import {
  OrderType,
  QuestionGroupStatus,
  QuestionGroupType,
  TagCategory,
  useGetUsedQSetQuestionsByQuestionIdsQuery,
  useSearchQuestionGroupsQuery,
} from '@exam-management/graphql';
import { Link } from '@exam-management/router';
import { Button, OpenInNewRoundedIcon } from '@shared/components';

import {
  FormItemSetQuestionChangeTable,
  type FormItemSetQuestionChangeTableProps,
  IncompleteTopicAndSubTopicOverlay,
  QuestionNotFoundOverlay,
} from '../../components';
import { useQuestionChangeInformation } from '../../providers';

export default function ItemSetQuestionChangeTableSection() {
  const { qSet } = useQSet();
  const { id: qSetId } = qSet;
  const { subTopicId, qBankStructureId, questionGroupSize, isInformationLoading } =
    useQuestionChangeInformation();
  if (isInformationLoading) {
    return null;
  }
  const { data: questionsGroupToChangeResult, loading: isLoadingQuestionsGroupToChange } =
    useSearchQuestionGroupsQuery({
      variables: {
        search: {
          status: [QuestionGroupStatus.Published],
          levelItemId: subTopicId,
          questionGroupType: [QuestionGroupType.ItemSet],
          refQBankStructureId: qBankStructureId,
          size: questionGroupSize,
        },
        paging: {
          pageSize: 200,
          currentPage: 0,
        },
        order: {
          field: 'refCode',
          type: OrderType.Desc,
        },
      },
      fetchPolicy: 'network-only',
      skip: !subTopicId,
    });

  const questionsGroupToChange =
    questionsGroupToChangeResult?.qctSearchQuestionGroups.questionGroups ?? [];
  const mapQuestion = questionsGroupToChange.map((questionGroup) => questionGroup.questions);
  const totalQuestionsGroupToChange =
    questionsGroupToChangeResult?.qctSearchQuestionGroups.total ?? 0;
  const questionIds = mapQuestion
    .flat()
    .map((question) => question?.id)
    .filter((id): id is string => typeof id === 'string');

  const { data: usedQSetQuestionsResult, loading: isLoadingUsedQSetQuestions } =
    useGetUsedQSetQuestionsByQuestionIdsQuery({
      variables: {
        input: {
          qSetId: qSetId,
          questionIds: questionIds.length ? questionIds : [''],
        },
      },
      fetchPolicy: 'network-only',
      skip: !questionIds.length,
    });

  const usedQSetQuestions =
    usedQSetQuestionsResult?.excGetQSetQuestionsByQuestionIds.qSetQuestions ?? [];

  const rows: FormItemSetQuestionChangeTableProps['rows'] = questionsGroupToChange.map(
    (questionGroup) => {
      const questions: FormItemSetQuestionChangeTableProps['rows'][number]['question'] = [];
      const isQuestionGroupUsedInQSet = usedQSetQuestions.some((usedQuestion) => {
        return questionGroup.questions?.some(
          (question) => question?.id === usedQuestion.questionId,
        );
      });
      for (const question of questionGroup.questions ?? []) {
        if (!question) continue;
        const difficulty = getDifficultyType(
          question.tags.find((tag) => tag.category === TagCategory.Difficulty)?.name ?? '',
        );
        const calculated =
          question.tags.find((tag) => tag.category === TagCategory.Calculation)?.name ?? 'คำนวณ';
        const usedQSetQuestion = usedQSetQuestions.find(
          (usedQuestion) => usedQuestion.questionId === question.id,
        );
        questions.push({
          qId: question.refCode,
          difficulty: difficulty,
          calculated: calculated === 'คำนวณ',
          questionStatus: usedQSetQuestion
            ? { used: true, questionNo: usedQSetQuestion.order }
            : { used: false },
          action: (
            <Link
              to='/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId'
              params={{
                structureId: question.refQBankStructureId,
                questionId: questionGroup.id,
                subQuestionId: question.id,
              }}
              target='_blank'
              rel='noreferrer'
            >
              <Button
                variant='outlined'
                color='secondary'
                size='small'
                startIcon={<OpenInNewRoundedIcon />}
              >
                ดูคำถาม
              </Button>
            </Link>
          ),
        });
      }
      return {
        id: questionGroup.id,
        name: questionGroup.name,
        questionGroupUsage: isQuestionGroupUsedInQSet,
        question: questions,
      };
    },
  );

  const isLoading = isLoadingQuestionsGroupToChange || isLoadingUsedQSetQuestions;

  return (
    <FormItemSetQuestionChangeTable
      name='questionGroupId'
      rows={isLoading ? [] : rows}
      total={isLoading ? 0 : totalQuestionsGroupToChange}
      loading={isLoading}
      slots={{
        noRowsOverlay: subTopicId ? (
          <QuestionNotFoundOverlay />
        ) : (
          <IncompleteTopicAndSubTopicOverlay />
        ),
      }}
    />
  );
}
